<template>
  <!-- Mounted by the shared map dialog's Overlays tab, which is always rendered (the
       markers' switch itself is in the Maps tab's More view): keeps the hazard markers
       on the page's map and owns the details panel a marker click opens. Teleported
       to <body> because the tab lives inside a hidden modal. -->
  <Teleport to="body">
    <template v-if="isPrimary">
      <HazardDetailSheet
        :open="open"
        :loading="loading"
        :failed="failed"
        :details="details"
        :panel-style="panelStyle"
        @close="close"
        @photo-error="onPhotoError"
      />
      <!-- The site's toast, as the POI sheet uses it. -->
      <div class="position-fixed top-0 end-0 p-3 hazard-toast-wrap">
        <div ref="toastEl" class="toast hazard-toast" role="status" aria-live="polite" aria-atomic="true">
          <div class="toast-body">{{ toastMessage }}</div>
        </div>
      </div>
    </template>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import { Toast } from 'bootstrap';
import HazardDetailSheet from './components/HazardDetailSheet.vue';
import graph_client from '../../utils/graphql/graph_client';
import { reportHandledError } from '../../utils/bugsnag';
import { sharedHazardLayer } from '../../utils/trailHazards/hazardLayerController';
import { HazardLoader } from '../../utils/trailHazards/hazardLoader';
import { buildHazardDetails } from '../../utils/trailHazards/hazardDetails';
import type { Hazard, HazardDetailsView } from '../../utils/trailHazards/hazardDetails';
import { hazardPanelStyle } from '../../utils/trailHazards/hazardPanelPlacement';
import { hazardScreen, hazardViewedEvent, trackHazardEvent } from '../../utils/trailHazards/hazardAnalytics';
import { linkedHazardCamera, linkedHazardIdFromUrl } from '../../utils/trailHazards/hazardLink';
import { isMapReady } from '../../utils/mapReadyHelper';
import emitter from '../../utils/bus';

// A page can render more than one map dialog (the live locator has two), and
// each one's Overlays tab mounts a host. All of them share the one layer, but only
// the first owns the panel, so a click never opens two.
let sheetOwner: symbol | null = null;

export default defineComponent({
  name: 'HazardsMapHost',
  components: { HazardDetailSheet },
  props: {
    /** Return true to ignore a marker click (e.g. while the planner draws a route). */
    ignoreClick: { type: Function as PropType<(event: any) => boolean>, default: null },
  },
  data() {
    return {
      isPrimary: false,
      open: false,
      loading: false,
      failed: false,
      details: null as HazardDetailsView | null,
      panelStyle: null as Record<string, string> | null,
      toastMessage: '',
      currentId: null as string | null,
      photoRefetched: false,
      justOpened: false,
    };
  },
  created() {
    (this as any).token = Symbol('hazards-host');
    (this as any).loader = new HazardLoader(graph_client as any, reportHandledError);
    if (!sheetOwner) {
      sheetOwner = (this as any).token;
      this.isPrimary = true;
    }
  },
  mounted() {
    const layer = sharedHazardLayer();
    layer.acquire();
    if (!this.isPrimary) return;

    layer.setClickHandler((id: string) => { this.openHazard(id); });
    layer.setClickFilter((event: any) => !!this.ignoreClick?.(event));
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.positionToMap);
    window.addEventListener('scroll', this.positionToMap, { passive: true });
    this.openLinkedHazard();
  },
  beforeUnmount() {
    const layer = sharedHazardLayer();
    if (this.isPrimary) {
      layer.setClickHandler(null);
      layer.setClickFilter(null);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.positionToMap);
      window.removeEventListener('scroll', this.positionToMap);
      (this as any).loader.cancel();
      this.stopWaitingForMap();
      sheetOwner = null;
    }
    layer.release();
  },
  methods: {
    /** `flyTo`: a `?hazard=` link, which also brings the pin into view. */
    async openHazard(id: string, { flyTo = false } = {}) {
      this.currentId = id;
      this.open = true;
      this.loading = true;
      this.failed = false;
      this.details = null;
      this.photoRefetched = false;
      // The click that opened the panel is still on its way up to the document,
      // where it would otherwise close the panel straight away.
      this.justOpened = true;
      window.setTimeout(() => { this.justOpened = false; }, 0);
      this.$nextTick(this.positionToMap);

      const result = await (this as any).loader.load(id);
      if (result.kind === 'stale') return;
      if (result.kind === 'gone') { this.leftTheMap(); return; }
      this.loading = false;
      if (result.kind === 'failed') { this.failed = true; return; }

      this.show(result.hazard);
      if (flyTo) this.flyToHazard(result.hazard);
      trackHazardEvent(hazardViewedEvent(result.hazard, hazardScreen()));
    },

    /**
     * A `?hazard=<id>` link (the Slack hazard notice's): open that hazard once
     * the map is up. `map-loaded` fires once and may already have gone by, so
     * the first style load counts too, as for a shared POI link.
     */
    openLinkedHazard() {
      const id = linkedHazardIdFromUrl(window.location.href);
      if (!id) return;
      if (isMapReady()) {
        this.openHazard(id, { flyTo: true });
        return;
      }
      const onMapReady = () => {
        this.stopWaitingForMap();
        this.openHazard(id, { flyTo: true });
      };
      (this as any).onMapReady = onMapReady;
      emitter.on('map-loaded', onMapReady);
      emitter.on('map-style-loaded', onMapReady);
    },

    stopWaitingForMap() {
      const onMapReady = (this as any).onMapReady;
      if (!onMapReady) return;
      emitter.off('map-loaded', onMapReady);
      emitter.off('map-style-loaded', onMapReady);
      (this as any).onMapReady = null;
    },

    flyToHazard(hazard: Hazard) {
      const map = (window as any).map;
      const camera = linkedHazardCamera(hazard, map?.getZoom?.());
      if (map && camera) map.flyTo(camera);
    },

    show(hazard: Hazard) {
      this.details = buildHazardDetails(hazard, new Date(), (key, values) => this.$t(key, values ?? {}) as string);
    },

    /** Null or no longer active: close, say so, and fetch the markers again. */
    leftTheMap() {
      this.close();
      this.showToast(this.$t('trail_hazards.no_longer_active') as string);
      sharedHazardLayer().refresh();
    },

    /**
     * A photo didn't load. Photo URLs change once the server has swapped in the
     * metadata-stripped copies, so fetch the hazard again — once — for fresh ones.
     */
    async onPhotoError() {
      if (this.photoRefetched || !this.currentId || !this.open) return;
      this.photoRefetched = true;
      const result = await (this as any).loader.load(this.currentId);
      if (result.kind === 'shown') this.show(result.hazard);
      else if (result.kind === 'gone') this.leftTheMap();
    },

    close() {
      this.open = false;
      this.loading = false;
      this.failed = false;
      this.details = null;
      this.currentId = null;
      (this as any).loader.cancel();
    },

    showToast(message: string) {
      this.toastMessage = message;
      const el = this.$refs.toastEl as HTMLElement | undefined;
      if (!el) return;
      try {
        Toast.getOrCreateInstance(el, { autohide: true, delay: 5000 }).show();
      } catch (error) {
        console.warn('[trail_hazards] toast failed', error);
      }
    },

    positionToMap() {
      if (!this.open) return;
      const map = document.querySelector('.mapboxgl-map');
      const dock = document.querySelector('[data-map-dock="right"]');
      this.panelStyle = hazardPanelStyle({
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        mapRect: map ? map.getBoundingClientRect() : null,
        dockLeft: dock ? dock.getBoundingClientRect().left : null,
      });
    },

    // Clicking the map elsewhere closes the panel, as it does the POI sheet.
    onDocumentClick(event: MouseEvent) {
      if (!this.open || this.justOpened) return;
      const target = event.target as Element | null;
      if (target?.closest?.('[data-hazard-sheet]') || target?.closest?.('.ds-lightbox')) return;
      this.close();
    },

    onKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape' && this.open) this.close();
    },
  },
});
</script>

<style scoped>
.hazard-toast-wrap {
  z-index: 9999;
  pointer-events: none;
}

.hazard-toast {
  pointer-events: auto;
}

.hazard-toast .toast-body {
  color: #000;
  background-color: #fff;
  border-radius: inherit;
}
</style>
